import { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const testimonials = [
  {
    text: "ServiceDesk Plus kurulumu sorunsuz ve hızlı gerçekleşti. WOTECH ekibi her aşamada yanımızdaydı. Destek süreçlerimiz %40 hızlandı, ekibimizin motivasyonu arttı.",
    author: 'Ahmet Yılmaz',
    position: 'IT Müdürü',
    company: 'Teknoloji A.Ş.',
    rating: 5,
    avatar: 'AY',
    avatarColor: '#0891B2',
  },
  {
    text: 'Endpoint Central ile patch otomasyonu kurumsal güvenliğimizi kökten değiştirdi. Güvenlik açıklarımızı %95 azalttık. Artık manuel patch yönetimi yapmıyoruz.',
    author: 'Ayşe Demir',
    position: 'Sistem Yöneticisi',
    company: 'Finans Holding',
    rating: 5,
    avatar: 'AD',
    avatarColor: '#F97316',
  },
  {
    text: "AutomMate entegrasyonu ile manuel süreçleri %70 azalttık. Ekibimiz artık stratejik işlere odaklanabiliyor. WOTECH'in desteği mükemmeldi.",
    author: 'Mehmet Kaya',
    position: 'CTO',
    company: 'E-Ticaret Ltd.',
    rating: 5,
    avatar: 'MK',
    avatarColor: '#0891B2',
  },
  {
    text: "Zoho ONE implementasyonu sayesinde tüm iş süreçlerimiz tek platformda birleşti. WOTECH'in danışmanlığı çok değerliydi; geçişimiz son derece sorunsuz oldu.",
    author: 'Zeynep Arslan',
    position: 'Operasyon Müdürü',
    company: 'Lojistik Group',
    rating: 5,
    avatar: 'ZA',
    avatarColor: '#F97316',
  },
  {
    text: 'Active Directory yönetimimiz AutomMate ile tamamen otomatikleşti. Kullanıcı işlemlerinde %80 zaman tasarrufu sağladık, insan hatası sıfıra indi.',
    author: 'Can Öztürk',
    position: 'IT Koordinatörü',
    company: 'Üretim Sanayi A.Ş.',
    rating: 5,
    avatar: 'CÖ',
    avatarColor: '#0891B2',
  },
];

const Testimonials = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from('.test-header', { scrollTrigger: { trigger: '.test-header', start: 'top 85%' }, opacity: 0, y: 30, duration: 0.9, ease: 'power3.out' });
      gsap.from('.test-sidebar-card', { scrollTrigger: { trigger: '.test-sidebar', start: 'top 75%' }, opacity: 0, x: 30, duration: 0.6, stagger: 0.1, ease: 'power3.out' });
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => setCurrent((p) => (p + 1) % testimonials.length), 6000);
    return () => clearInterval(interval);
  }, []);

  const t = testimonials[current];

  return (
    <section ref={sectionRef} id="testimonials" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="test-header text-center mb-14">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#ECFEFF] border border-[#0891B2]/30 text-[#0E7490] text-xs font-semibold mb-5 font-body">
            <i className="ri-chat-quote-line"></i>
            Müşteri Yorumları
          </div>
          <h2 className="text-4xl font-extrabold text-[#0F172A] mb-4 font-heading">
            Müşterilerimiz Ne Diyor?
          </h2>
          <p className="text-base text-[#475569] font-body">Gerçek kullanıcılardan gerçek sonuçlar.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main testimonial */}
          <div className="lg:col-span-2">
            <div
              key={current}
              className="h-full p-8 md:p-10 rounded-2xl bg-[#F8FAFC] border border-[#E2E8F0] flex flex-col"
              style={{ animation: 'fadeIn 0.5s ease-out' }}
            >
              {/* Stars */}
              <div className="flex gap-1 mb-6">
                {[...Array(t.rating)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-xl text-[#F97316]"></i>
                ))}
              </div>

              <i className="ri-double-quotes-l text-4xl text-[#0891B2]/20 mb-3"></i>
              <p className="text-lg text-[#334155] leading-relaxed flex-grow font-body font-medium mb-8">
                {t.text}
              </p>

              <div className="flex items-center gap-4 pt-5 border-t border-[#E2E8F0]">
                <div
                  className="w-12 h-12 flex items-center justify-center rounded-xl text-white font-extrabold text-sm font-heading flex-shrink-0"
                  style={{ background: t.avatarColor }}
                >
                  {t.avatar}
                </div>
                <div>
                  <div className="font-bold text-[#0F172A] font-heading">{t.author}</div>
                  <div className="text-sm text-[#0891B2] font-semibold font-body">{t.position}</div>
                  <div className="text-xs text-[#64748B] font-body">{t.company}</div>
                </div>
              </div>
            </div>

            {/* Dot navigation */}
            <div className="flex items-center gap-2 mt-5">
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrent(i)}
                  className="rounded-full transition-all duration-300 cursor-pointer"
                  style={{ width: current === i ? '24px' : '8px', height: '8px', background: current === i ? '#0891B2' : '#CBD5E1' }}
                  aria-label={`Yorum ${i + 1}`}
                />
              ))}
              <button onClick={() => setCurrent((p) => (p - 1 + testimonials.length) % testimonials.length)} className="ml-2 w-8 h-8 flex items-center justify-center rounded-lg border border-[#E2E8F0] text-[#475569] hover:text-[#0891B2] hover:border-[#0891B2]/40 transition-all duration-200 cursor-pointer">
                <i className="ri-arrow-left-s-line text-xl"></i>
              </button>
              <button onClick={() => setCurrent((p) => (p + 1) % testimonials.length)} className="w-8 h-8 flex items-center justify-center rounded-lg border border-[#E2E8F0] text-[#475569] hover:text-[#0891B2] hover:border-[#0891B2]/40 transition-all duration-200 cursor-pointer">
                <i className="ri-arrow-right-s-line text-xl"></i>
              </button>
            </div>
          </div>

          {/* Sidebar — mini cards */}
          <div className="test-sidebar flex flex-col gap-4">
            {testimonials.filter((_, i) => i !== current).slice(0, 3).map((item, i) => (
              <button
                key={i}
                onClick={() => setCurrent(testimonials.indexOf(item))}
                className="test-sidebar-card text-left p-5 rounded-xl bg-[#F8FAFC] border border-[#E2E8F0] hover:border-[#0891B2]/30 transition-all duration-200 cursor-pointer"
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg text-white text-xs font-extrabold font-heading flex-shrink-0" style={{ background: item.avatarColor }}>
                    {item.avatar}
                  </div>
                  <div>
                    <div className="text-xs font-bold text-[#0F172A] font-heading">{item.author}</div>
                    <div className="text-xs text-[#64748B] font-body">{item.company}</div>
                  </div>
                </div>
                <p className="text-xs text-[#475569] leading-relaxed line-clamp-2 font-body">{item.text}</p>
              </button>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
        .line-clamp-2 { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
      `}</style>
    </section>
  );
};

export default Testimonials;
